import React, { useMemo, useRef, useState } from 'react';
import { makeStyles, Tooltip, useMediaQuery } from '@material-ui/core';
import classNames from 'classnames';
import { useGetLevelOfSkillsQuery } from 'utils/wemble-api';
import { LevelOfSkillDto } from 'utils/wemble-api.generated';
import { haptic } from 'utils/haptics';

export const LEVEL_FILLED = '#75B7D8';
export const LEVEL_FILLED_ZERO = '#65C898';

const useStyles = makeStyles((theme) => ({
  track: {
    display: 'flex',
    alignItems: 'center',
  },
  track_row: {
    gap: 4,
    width: '100%',
  },
  track_inline: {
    gap: 2,
    flexShrink: 0,
  },
  // One rectangle per level step, so a track reads as a meter filling left to
  // right rather than a line of look-alike dots.
  segment: {
    boxSizing: 'border-box',
    transition: 'background-color 0.12s, opacity 0.12s',
  },
  // Table rows (person-card Grid, competence People tab): segments share the
  // width, lined up under the level columns.
  segment_row: {
    flex: 1,
    minWidth: 0,
    height: 28,
    borderRadius: 4,
    border: `1px solid ${theme.palette.gray.g6}`,
    backgroundColor: theme.palette.gray.g7,
  },
  // Inline next to a name (person-card Current). The tile behind is g7, so an
  // empty step uses the darker grey.
  segment_inline: {
    width: 18,
    height: 14,
    borderRadius: 3,
    backgroundColor: theme.palette.gray.g9,
  },
  segment_inlineCompact: {
    width: 13,
  },
  segment_filled: {
    border: 'none',
  },
  segment_clickable: {
    cursor: 'pointer',
    // App-wide touch conventions: no grey iOS tap flash, no text selection on
    // long-press.
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const, // no iOS long-press menu
    userSelect: 'none',
  },
}));

/** How one segment looks given the held level and the level being hovered:
 *  hover previews what a click would do — fill ahead (half opacity) for a
 *  higher level, fade back what would be dropped, fade the whole run when the
 *  click would clear it. */
const segmentStyle = (levelValue: number, held: number, target: number | null): React.CSSProperties => {
  const isFilled = held >= 0 && levelValue <= held;
  const colorFor = (level: number) => (level === 0 ? LEVEL_FILLED_ZERO : LEVEL_FILLED);
  if (target === null) return isFilled ? { backgroundColor: colorFor(held) } : {};
  if (target === held) return isFilled ? { backgroundColor: colorFor(held), opacity: 0.35 } : {};
  if (levelValue <= target) return { backgroundColor: colorFor(target), opacity: isFilled ? 1 : 0.45 };
  return isFilled ? { backgroundColor: colorFor(held), opacity: 0.35 } : {};
};

/** A person's level on one competence as a segmented track — the one
 *  editable level control shared by the person card (Current and Grid) and the
 *  competence People tab.
 *
 *  Clicking a level sets it; clicking the level already held clears it. Right
 *  after a click the pointer is still resting on the level just set, which by
 *  that rule would now offer to clear it — so the clicked segment stays quiet
 *  (shows the new state, further clicks ignored)
 *  until the pointer leaves it. That also stops a double-click from undoing
 *  itself. Touch has no resting pointer or preview, so a tap on the held level
 *  clears it directly. */
// Tooltips only name the level; what a click does is shown by the hover
// preview, and "Set to…/Clear…" wording on top of that was confusing.
const SkillLevelTrack = ({
  level: held = -1,
  editable = false,
  onChange,
  size = 'row',
}: {
  /** Level held; -1 when not held or held without a level. */
  level?: number;
  editable?: boolean;
  /** The level to set, or null to clear. */
  onChange?: (level: LevelOfSkillDto | null) => void;
  size?: 'row' | 'inline';
}) => {
  const classes = useStyles();
  const { data: levelOfSkills } = useGetLevelOfSkillsQuery();
  const canHover = useMediaQuery('(hover: hover)');
  const [hovered, setHovered] = useState<number | null>(null);
  const [justSet, setJustSet] = useState<number | null>(null);
  // Touch has no "pointer left" to end the guard below, so a quick second tap
  // on the same bar (a double tap) is ignored instead — otherwise it would set
  // a level and immediately clear it again.
  const lastTap = useRef<{ level: number; at: number } | null>(null);

  const levels = useMemo<LevelOfSkillDto[]>(
    () => [...(levelOfSkills ?? [])].sort((a, b) => (a.level ?? 0) - (b.level ?? 0)),
    [levelOfSkills],
  );
  const canEdit = editable && !!onChange;
  // Only preview where hover exists: touch fires mouseenter on tap but never
  // mouseleave, which would leave the preview stuck.
  const previewing = canEdit && canHover && justSet === null ? hovered : null;

  const select = (level: LevelOfSkillDto, levelValue: number, isCurrent: boolean) => {
    if (!canEdit || !level._id || justSet === levelValue) return;
    if (!canHover) {
      const now = Date.now();
      if (lastTap.current && lastTap.current.level === levelValue && now - lastTap.current.at < 600) return;
      lastTap.current = { level: levelValue, at: now };
    }
    haptic('medium');
    onChange!(isCurrent ? null : level);
    if (canHover) setJustSet(levelValue);
  };

  return (
    <div className={classNames(classes.track, size === 'row' ? classes.track_row : classes.track_inline)}>
      {levels.map((level, i) => {
        const levelValue = level.level ?? 0;
        const isCurrent = held >= 0 && levelValue === held;
        const style = segmentStyle(levelValue, held, previewing);
        return (
          <Tooltip key={level._id || i} title={level.name ?? ''} arrow placement="top">
            <div
              className={classNames(
                classes.segment,
                size === 'row' ? classes.segment_row : classes.segment_inline,
                size === 'inline' && levels.length > 5 && classes.segment_inlineCompact,
                style.backgroundColor && classes.segment_filled,
                canEdit && classes.segment_clickable,
              )}
              style={style}
              onClick={(e) => {
                // Tracks sit inside rows and tiles that have their own click.
                e.stopPropagation();
                select(level, levelValue, isCurrent);
              }}
              onMouseEnter={canEdit && canHover ? () => setHovered(levelValue) : undefined}
              onMouseLeave={
                canEdit && canHover
                  ? () => {
                      setHovered(null);
                      setJustSet(null);
                    }
                  : undefined
              }
            />
          </Tooltip>
        );
      })}
    </div>
  );
};

export default SkillLevelTrack;
